import type { ColumnDef } from '@tanstack/react-table';

import { type GetOrganizationUsersQuery, UserPermission, UserState } from 'data/__generated__';
import { multiSelectColumn, dateColumn } from 'components/DataTable';

import { StatusTag } from 'pages/Users/components/StatusTag';
import { USER_STATES_LABELS } from 'pages/Users/constants/states';

export type EmbeddedUserRow = GetOrganizationUsersQuery['organizationUsers'][number];

export type EmbeddedUsersFilterInput = {
    searchValue: string;
    states: UserState[];
};

// Web-app account roles (vs "employee" consumers). Used to split an organization's users into the two tables.
export const WEB_APP_USER_GROUPS: UserPermission[] = [
    UserPermission.Admin,
    UserPermission.Editor,
    UserPermission.Analyst,
];

export const isWebAppUser = (user: EmbeddedUserRow): boolean =>
    user.permissions.some((permission) => WEB_APP_USER_GROUPS.includes(permission.userGroup));

const EmptyCell = () => <span className="text-muted-foreground">—</span>;

const userRoles = (user: EmbeddedUserRow): string =>
    [...new Set(user.permissions.map((permission) => permission.userGroup))].join(', ');

export const embeddedUsersColumns: ColumnDef<EmbeddedUserRow, unknown>[] = [
    {
        id: 'fullname',
        header: 'Nom',
        meta: { defaultVisible: true },
        accessorFn: (row) => row.profile.fullname || '',
        cell: ({ row }) => row.original.profile.fullname || <EmptyCell />,
    },
    {
        accessorKey: 'username',
        header: 'Email',
        meta: { defaultVisible: true },
    },
    {
        id: 'roles',
        header: 'Rôles',
        meta: { defaultVisible: true },
        accessorFn: (row) => userRoles(row),
        cell: ({ row }) => userRoles(row.original) || <EmptyCell />,
    },
    multiSelectColumn<EmbeddedUserRow, EmbeddedUsersFilterInput>({
        id: 'state',
        accessorFn: (row) => row.profile.state,
        header: 'Statut',
        filterKey: 'states',
        defaultVisible: true,
        options: Object.values(UserState).map((state) => ({ value: state, label: USER_STATES_LABELS[state] })),
        renderOption: (option) => <StatusTag state={option.value as UserState} />,
        cell: ({ row }) => <StatusTag state={row.original.profile.state} />,
    }),
    dateColumn<EmbeddedUserRow, EmbeddedUsersFilterInput>({
        accessorKey: 'lastSeenAt',
        header: 'Dernière connexion',
        defaultVisible: true,
        cell: ({ getValue }) => {
            const value = getValue() as number | null | undefined;
            return value ? new Date(value).toLocaleString('fr-FR') : 'jamais';
        },
    }),
    {
        id: 'lastSite',
        header: 'Dernier site',
        accessorFn: (row) => row.lastSite?.name ?? '',
        cell: ({ row }) => row.original.lastSite?.name || <EmptyCell />,
    },
];
